/* eslint-disable no-param-reassign */
import { Boundary, ImageLoader } from 'components/common';
import { Formik } from 'formik';
import {
  useDocumentTitle,
  useFileHandler,
  useModal,
  useScrollTop
} from 'hooks';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from 'redux/actions/miscActions';
import { updateProfile } from 'redux/actions/profileActions';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import ConfirmModal from './ConfirmModal';
import EditForm from './EditForm';
import defaultAvatar from '../../../images/defaultAvatar.png';
import defaultBanner from '../../../images/defaultBanner.jpg';

const EditProfile = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('titleEditAccount'));
  useScrollTop();

  const FormSchema = Yup.object().shape({
    fullname: Yup.string()
      .min(3, t('accountNameError1'))
      .max(60, t('accountNameError2'))
      .required(t('accountNameMandatory')),
    email: Yup.string()
      .email(t('signInNotValidEmail'))
      .required(t('signInMandatoryEmail')),
    address: Yup.object().shape({
      country: Yup.string(),
      region: Yup.string(),
      province: Yup.string(),
      municipality: Yup.string(),
      nap: Yup.string()
    }),
    mobile: Yup.object().shape({
      country: Yup.string(),
      countryCode: Yup.string(),
      dialCode: Yup.string(),
      value: Yup.string()
    })
  });

  const modal = useModal();
  const dispatch = useDispatch();

  useEffect(
    () => () => {
      dispatch(setLoading(false));
    },
    []
  );

  const { profile, auth, isLoading } = useSelector((state) => ({
    profile: state.profile,
    auth: state.auth,
    isLoading: state.app.loading
  }));

  const initFormikValues = {
    fullname: profile.fullname || '',
    email: profile.email || '',
    address: profile.address || {},
    mobile: profile.mobile || {}
  };

  const { imageFile } = useFileHandler({
    avatar: {},
    banner: {}
  });

  const update = (form, credentials = {}) => {
    dispatch(
      updateProfile({
        updates: {
          fullname: form.fullname,
          email: form.email,
          address: form.address,
          mobile: form.mobile,
          avatar: profile.avatar,
          banner: profile.banner
        },
        files: {
          bannerFile: imageFile.banner.file,
          avatarFile: imageFile.avatar.file
        },
        credentials
      })
    );
  };

  const onConfirmUpdate = (form, password) => {
    if (password) {
      update(form, { email: form.email, password });
    }
  };

  const onSubmitUpdate = (form) => {
    const fieldsChanged = Object.keys(form).some(
      (key) => profile[key] !== form[key]
    );

    if (
      fieldsChanged ||
      Boolean(imageFile.banner.file || imageFile.avatar.file)
    ) {
      if (form.email !== profile.email) {
        modal.onOpenModal();
      } else {
        if (form.address.country === 'Svizzera') {
          delete form.address.region;
          delete form.address.province;
          delete form.address.municipality;
        } else if (form.address.country === 'Italia') {
          delete form.address.npa;
        }
        update(form);
      }
    }
  };

  return (
    <Boundary>
      <div className="edit-user">
        <h3 className="text-center">{t('accountMainMessage')}</h3>
        <Formik
          initialValues={initFormikValues}
          validateOnChange
          validationSchema={FormSchema}
          onSubmit={onSubmitUpdate}
        >
          {() => (
            <>
              <div className="user-profile-banner">
                <div className="user-profile-banner-wrapper">
                  <ImageLoader
                    alt="Banner"
                    className="user-profile-banner-img"
                    src={
                      imageFile.banner.url || profile.banner || defaultBanner
                    }
                  />
                </div>
                <div className="user-profile-avatar-wrapper">
                  <ImageLoader
                    alt="Avatar"
                    className="user-profile-img"
                    src={
                      imageFile.avatar.url || profile.avatar || defaultAvatar
                    }
                  />
                </div>
              </div>
              <EditForm authProvider={auth.provider} isLoading={isLoading} />
              <ConfirmModal onConfirmUpdate={onConfirmUpdate} modal={modal} />
            </>
          )}
        </Formik>
      </div>
    </Boundary>
  );
};

export default EditProfile;
